import { Paper, Table as MUITable, TableBody as MuiTableBody, TableContainer, TableRow as MuiTableRow, TableCell, Box, alpha } from '@mui/material';
import { useEffect, useState } from 'react';

import { TableFilter } from './table-filter';
import { TableHead } from './table-head';
import { TablePagination } from './table-pagination';
import { TableRow } from './table-row';
import { TableSkeleton } from './table-skeleton';
import { TableData, TableProps } from 'src/types/table';


export const Table = ({
  columns,
  data,
  onSort,
  onFilter,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  showCheckbox = false,
  selectedRows = [],
  onSelectRows,
  isRowSelectable,
  loading = false,
  onRowClick,
  showPagination = true,
  hideRowsPerPage = false,
  compactPagination = false,
  TableContainerProps,
  MUITableProps,
  resetSortFlag,
}: TableProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastDataLength, setLastDataLength] = useState(0);

  // Track when data is being refreshed vs initially loaded
  useEffect(() => {
    if (loading && data.length > 0) {
      setIsRefreshing(true);
    } else if (!loading) {
      setIsRefreshing(false);
    }
  }, [loading, data.length]);

  // Track data length changes for progressive loading indicators
  useEffect(() => {
    if (data.length !== lastDataLength) {
      setLastDataLength(data.length);
    }
  }, [data.length, lastDataLength]);

  const handleHeaderClick = (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
  ): void => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(columnId);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const handleSort = (columnId: string): void => {
    let newDirection: 'asc' | 'desc' | null = 'asc';
    if (sortColumn === columnId) {
      if (sortDirection === 'asc') newDirection = 'desc';
      else if (sortDirection === 'desc') newDirection = null;
    }

    setSortDirection(newDirection);
    setSortColumn(newDirection ? columnId : null);
    onSort?.(columnId, newDirection);
  };

  const handleFilter = (columnId: string, value: string): void => {
    setFilterValue(value);
    onFilter?.(columnId, value);
  };

  const selectableIds = (
    isRowSelectable ? data.filter((row) => isRowSelectable(row)) : data
  ).map((row) => String(row.id));

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    if (selectableIds.length > 0 && selectableIds.every((id) => selectedRows.includes(id))) {
      onSelectRows?.(selectedRows.filter((id) => !selectableIds.includes(id)));
    } else {
      onSelectRows?.(
        selectedRows.concat(selectableIds.filter((id) => !selectedRows.includes(id)))
      );
    }
  };

  const handleSelectRow = (id: string): void => {
    const row = data.find((item) => String(item.id) === id);
    if (row && isRowSelectable && !isRowSelectable(row)) return;
    onSelectRows?.(
      selectedRows.includes(id)
        ? selectedRows.filter((rowId) => rowId !== id)
        : [...selectedRows, id]
    );
  };

  const isAllSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedRows.includes(id));
  const isPartiallySelected =
    !isAllSelected && selectableIds.some((id) => selectedRows.includes(id));

  useEffect(() => {
    setSortColumn(null);
    setSortDirection(null);
  }, [resetSortFlag]);

  return (
    <>
      <TableContainer component={Paper} sx={TableContainerProps?.sx}>
        {/* Progressive loading indicator */}
        {isRefreshing && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              zIndex: 1,
            }}>
            <Box
              sx={{
                height: '100%',
                bgcolor: 'primary.main',
                animation: 'progressBar 1.5s ease-in-out infinite',
                '@keyframes progressBar': {
                  '0%': {
                    width: '0%',
                    marginLeft: '0%',
                  },
                  '50%': {
                    width: '75%',
                    marginLeft: '12.5%',
                  },
                  '100%': {
                    width: '0%',
                    marginLeft: '100%',
                  },
                },
              }}
            />
          </Box>
        )}

        <MUITable
          sx={{
            ...MUITableProps?.sx,
            opacity: isRefreshing ? 0.7 : 1,
            transition: 'opacity 0.2s ease',
            '& td': {
              fontSize: '12px',
            },
          }}>
          <TableHead
            columns={columns}
            onHeaderClick={handleHeaderClick}
            onSort={handleSort}
            sortDirection={sortDirection}
            sortColumn={sortColumn}
            showCheckbox={showCheckbox}
            onSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            isPartiallySelected={isPartiallySelected}
          />
          <MuiTableBody>
            {loading && data.length === 0 ? (
              <TableSkeleton
                rowsPerPage={rowsPerPage ?? 4}
                columns={showCheckbox ? columns.length + 1 : columns.length}
              />
            ) : data.length > 0 ? (
              data.map((row: TableData) => (
                <TableRow
                  key={String(row.id)}
                  row={row}
                  columns={columns}
                  showCheckbox={showCheckbox}
                  selectedRows={selectedRows}
                  onSelectRow={handleSelectRow}
                  onRowClick={onRowClick}
                  isRowSelectable={isRowSelectable}
                />
              ))
            ) : (
              <MuiTableRow>
                <TableCell
                  colSpan={showCheckbox ? columns.length + 1 : columns.length}
                  align="center">
                  No data found
                </TableCell>
              </MuiTableRow>
            )}
          </MuiTableBody>
        </MUITable>

        <TableFilter
          anchorEl={anchorEl}
          activeColumn={activeColumn}
          columns={columns}
          sortDirection={sortDirection}
          filterValue={filterValue}
          onClose={handleClose}
          onSort={handleSort}
          onFilter={handleFilter}
        />
      </TableContainer>
      {showPagination && (
        <TablePagination
          page={page ?? 0}
          rowsPerPage={rowsPerPage ?? 10}
          totalRows={totalRows ?? 0}
          onPageChange={onPageChange ?? ((): void => { })}
          onRowsPerPageChange={onRowsPerPageChange ?? ((): void => { })}
          hideRowsPerPage={hideRowsPerPage}
          compact={compactPagination}
        />
      )}
    </>
  );
};
